import React, { ReactNode } from 'react'
import DialogContainer from 'react-md/lib/Dialogs/DialogContainer'
import DefaultTitle, { DialogTitleProps } from './Title'
import DefaultActions, { DialogActionsProps } from './Actions'
import pick from 'lodash/pick'
import styled from 'styled-components'
import classnames from 'classnames'

export type DialogLayoutProps = {
  children: ReactNode
  dialogTitleRenderer?: React.FC<DialogTitleProps>
  dialogActionsRenderer?: React.FC<DialogActionsProps> | null
  dialogClassName?: string
  dialogClass?: string
  dialogId?: string
  type?: string
} & DialogActionsProps &
  DialogTitleProps &
  typeof defaultProps

const defaultProps = {
  dialogTitleRenderer: DefaultTitle,
  dialogActionsRenderer: DefaultActions,
  dialogClassName: '',
  titleClassName: '',
  contentClassName: '',
  footerClassName: '',
  dialogContainerClassName: '',
  dialogId: '',
  type: '',
  autopadContent: false,
}

function DialogLayout(props: DialogLayoutProps) {
  const {
    children,
    dialogTitleRenderer: DialogTitle,
    dialogActionsRenderer: DialogActions,
    dialogClassName,
    dialogContainerClassName,
    dialogId,
    type = '',
    titleClassName,
    footerClassName,
    title,
  } = props

  return (
    <StyledDialogContainer
      visible
      portal
      modal
      autosizeContent={false}
      autopadContent={false}
      id={`${dialogId}_dialog`}
      type={type}
      renderNode={document.body}
      dialogClassName={classnames('dialog', { [dialogClassName]: dialogClassName })}
      className={classnames('dialog__container', { [dialogContainerClassName]: dialogContainerClassName })}
      title={title}
      titleClassName={classnames('dialog__title', { [titleClassName]: titleClassName })}
      footerClassName={classnames('dialog__footer', { [footerClassName]: footerClassName })}
      focusOnMount={false}
      actions={
        DialogActions ? (
          <DialogActions
            {...pick(props, [
              'onContinue',
              'onCancel',
              'onExtra',
              'isProcessing',
              'role_level',
              'continueLabel',
              'cancelLabel',
              'extraLabel',
              'isNewDesign',
              'hasContinue',
              'hasCancel',
              'hasExtra',
              'continueIsDisabled',
              'context',
            ])}
          />
        ) : null
      }
    >
      {children}
    </StyledDialogContainer>
  )
}

const StyledDialogContainer = styled(DialogContainer)`
  background: rgba(0, 0, 0, 0.7);
  animation: fade 0.1s linear;
  @keyframes fade {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }

  &.snackbar {
    z-index: 9999;
  }

  .dialog {
    min-width: 360px;

    ${({ theme }) => theme.mediaQueries.medium} {
      min-width: 500px;
    }

    min-height: 200px;
    box-shadow: none;
    border-radius: 20px;

    &--danger {
      .md-dialog-footer {
        .outline-primary {
          border-color: #DD5151;
          color: #DD5151;
        }
        .primary {
          background: #DD5151;
        }
      } 
    }

    &--medium {
      width: 610px;
    }

    &--medium-1 {
      width: 510px;
    }

    &--large {
      width: 860px;
    }

    &--extraLarge {
      width: 900px;
    }

    &--2extraLarge {
      width: 90%;
      max-width: 1400px;
    }

    &__title {
      font-size: 24px;
      font-weight: bold;
      text-align: center;
      text-transform: capitalize;
      padding: 40px 40px 0;
      padding-bottom: 0
      color: ${({ theme }) => theme.colors.secondary};

      &--small {
        font-size: 20px;
      }

      &--no-capitalize {
        text-transform: none;
      }

      &--less-padding {
      padding: 20px 40px 0;
      }

      &--custom {
        padding: 0;
      }
    }

    &__footer {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px;

      &--less-padding {
        padding: 30px;
      }

      &--lesser-padding {
        padding: 20px;
      }

      &--no-padding {
        padding: 0;
      }
      
      &--no-footer {
        display: none !important;
      }

    }

    ${({ theme }) => theme.mediaQueries.smallOnly} {
      &__footer {
        padding: 24px;
      }
    }

    &.check-in-members-dialog {
      top: 0;
      left: 0;
      transform: unset;
      height: 100%;
      max-height: unset;
      border-radius: 0;

      &-body {
        height: calc(100vh - 136px);
      }

      .dialog {
        &__title {
          display: none;
        }

        &__footer {
          button {
            &:first-child {
              width: 116px;
            }

            &:last-child {
              width: 195px;
            }
          }
        }
      }
    }

    &.check-in-members-kiosk-dialog {
      max-width: 100%;
      width: 100%;
    }

    &.snackbar-dialog {
      &--message-body {
        padding: 0 40px;
        margin-top: 28px;
        text-align: center;
        margin-bottom: 43px;
        font-weight: normal;
        font-size: 18px;
        line-height: 24px;
      }
    }
  }

  ${({ theme }) => theme.mediaQueries.smallOnly} {
    .dialog {
      &.reset-password-dialog {
        min-width: 100%;
        min-height: 100%;
        border-radius: 0;
      }

      .reset-password-dialog-body {
        height: 90vh;
        overflow: auto;

        &::-webkit-scrollbar {
          width: 5px;
          background-color: #F5F5F5;
          border-radius: 5px;
        }

        &::-webkit-scrollbar-thumb {
          background-color: ${({ theme }) => theme.colors.label};
          border-radius: 50px;
        }
      }
    }
  }
`

DialogLayout.defaultProps = defaultProps

export default DialogLayout
